import React, { useState, useEffect } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import '../assets/css/styles.min.css';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import LoadingSpinner from '../components/LoadingSpinner';
import Swal from 'sweetalert2';

const ServiceManagement = () => {
    const [services, setServices] = useState([]);
    const [modalService, setModalService] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [editService, setEditService] = useState({
        id: '',
        title: '',
        description: '',
        price: '',
        status: '',
        image_path: '',
        image_data: null
    });
    const [newImage, setNewImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    
    // Search and pagination states
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // Default service image
    const defaultServiceImage = 'https://i.postimg.cc/m2sq9Tdm/Adobe-Stock-1257463272.jpg';

    useEffect(() => {
        fetchServices();
    }, []);

    const fetchServices = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get('https://api.codecircle.co/api/site-services');
            setServices(response.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            Swal.fire('Error', 'Failed to load services', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // Filter services based on search
    const filteredServices = services.filter(service => 
        service.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        service.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        service.price?.toString().includes(searchQuery.toLowerCase()) ||
        service.status?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentServices = filteredServices.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredServices.length / itemsPerPage);

    const openModal = (service) => {
        setModalService(service);
        setEditService({
            ...service,
            price: service.price ? service.price.toString() : ''
        });
        setImagePreview(formatImageUrl(service));
    };

    const closeModal = () => {
        setModalService(null);
        setNewImage(null);
        setImagePreview(null);
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            
            // Validate file type
            const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
            if (!validTypes.includes(file.type)) {
                Swal.fire('Error', 'Invalid file type. Please upload JPEG, PNG, JPG, or GIF image.', 'error');
                return;
            }

            // Validate file size (5MB max)
            if (file.size > 5 * 1024 * 1024) {
                Swal.fire('Error', 'Image size too large. Maximum size is 5MB.', 'error');
                return;
            }
            
            setNewImage(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const saveChanges = async () => {
        try {
            // Validate input
            if (!editService.title.trim()) {
                Swal.fire('Error', 'Title is required', 'error');
                return;
            }
            
            if (!editService.description.trim()) {
                Swal.fire('Error', 'Description is required', 'error');
                return;
            }

            if (!editService.price.toString().trim()) {
                Swal.fire('Error', 'Price is required', 'error');
                return;
            }

            // Create FormData object
            const formData = new FormData();
            formData.append('title', editService.title.trim());
            formData.append('description', editService.description.trim());
            formData.append('price', editService.price.toString().trim());
            formData.append('status', editService.status);
            formData.append('_method', 'PUT'); // For Laravel PUT method simulation
            
            if (newImage) {
                formData.append('image', newImage);
            }

            // Show loading state
            Swal.fire({
                title: 'Updating service...',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const response = await axios.post(
                `https://api.codecircle.co/api/site-services/${editService.id}`, 
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'Accept': 'application/json'
                    }
                }
            );

            // Close loading indicator
            Swal.close();

            // Update UI
            setServices(services.map((service) => 
                service.id === editService.id ? response.data : service
            ));
            
            closeModal();
            await fetchServices(); // Refresh data to get updated image
            Swal.fire('Success', 'Service updated successfully', 'success');
        } catch (error) {
            console.error('Error updating service:', error);
            
            let errorMessage = 'Failed to update service';
            
            if (error.response?.status === 422 && error.response?.data?.errors) {
                errorMessage = Object.entries(error.response.data.errors)
                    .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
                    .join('\n');
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }
            
            Swal.fire('Error', errorMessage, 'error');
        }
    };

    const deleteService = (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.delete(`https://api.codecircle.co/api/site-services/${id}`);
                    setServices(services.filter((service) => service.id !== id));
                    Swal.fire('Deleted!', 'Service has been deleted.', 'success');
                } catch (error) {
                    console.error('Error deleting service:', error);
                    Swal.fire('Error', 'Failed to delete service', 'error');
                }
            }
        });
    };

    const formatImageUrl = (service) => {
        if (!service) return defaultServiceImage;
        
        if (service.image_data) {
            return `data:image/jpeg;base64,${service.image_data}`;
        }
        
        if (service.image_path) {
            const imagePath = service.image_path.startsWith('/') 
                ? service.image_path.substring(1) 
                : service.image_path;
                
            return `https://api.codecircle.co/storage/${imagePath}`;
        }
        
        return defaultServiceImage;
    };

    if (isLoading) {
        return (
            <div className="page-wrapper">
                <div className="body-wrapper">
                    <Header />
                    <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 70px)' }}>
                        <LoadingSpinner />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-wrapper" id="main-wrapper" data-layout="vertical" data-navbarbg="skin6" data-sidebartype="full" data-sidebar-position="fixed" data-header-position="fixed">
            <Sidebar />
            <div className="body-wrapper">
                <Header />
                <div className="container-fluid">
                    <div className="card">
                        <div className="card-body">
                            <h5 className="card-title fw-semibold mb-4">Service Management</h5>

                            {/* Search and Controls Section */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="d-flex gap-3 align-items-center">
                                    <div className="search-box">
                                        <div className="input-group" style={{ width: '250px' }}>
                                            <span className="input-group-text border-primary bg-primary">
                                                <i className="fas fa-search text-white"></i>
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control border-primary"
                                                placeholder="Search services..."
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <select
                                        className="form-select border-primary"
                                        style={{ width: '100px' }}
                                        value={itemsPerPage}
                                        onChange={(e) => {
                                            setItemsPerPage(Number(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value="5">5</option>
                                        <option value="10">10</option>
                                        <option value="20">20</option>
                                        <option value="50">50</option>
                                    </select>
                                </div>
                                <a href="/add-service" className="btn btn-primary">
                                    <i className="fas fa-plus me-2"></i>Add Service
                                </a>
                            </div>

                            {/* Table Section */}
                            <div className="table-responsive">
                                <table className="table text-nowrap align-middle mb-0">
                                    <thead>
                                        <tr className="border-2 border-bottom border-primary border-0">
                                            <th scope="col">ID</th>
                                            <th scope="col">Image</th>
                                            <th scope="col">Title</th>
                                            <th scope="col">Description</th>
                                            <th scope="col">Price</th>
                                            <th scope="col">Status</th>
                                            <th scope="col" className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentServices.length > 0 ? (
                                            currentServices.map((service) => (
                                                <tr key={service.id}>
                                                    <td>{service.id}</td>
                                                    <td>
                                                        <img
                                                            src={formatImageUrl(service)}
                                                            alt={service.title}
                                                            width="50"
                                                            height="50"
                                                            className="object-fit-cover rounded"
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.src = defaultServiceImage;
                                                            }}
                                                        />
                                                    </td>
                                                    <td>{service.title}</td>
                                                    <td>
                                                        {service.description.length > 50 
                                                            ? `${service.description.substring(0, 50)}...` 
                                                            : service.description}
                                                    </td>
                                                    <td>${service.price}</td>
                                                    <td>
                                                        <span className={`badge bg-${service.status === 'active' ? 'success' : 'danger'}`}>
                                                            {service.status}
                                                        </span>
                                                    </td>
                                                    <td className="text-center">
                                                        <button 
                                                            className="btn btn-sm btn-info me-2" 
                                                            onClick={() => openModal(service)}
                                                            title="Edit"
                                                        >
                                                            <i className="fas fa-edit"></i>
                                                        </button>
                                                        <button 
                                                            className="btn btn-sm btn-danger" 
                                                            onClick={() => deleteService(service.id)}
                                                            title="Delete"
                                                        >
                                                            <i className="fas fa-trash"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center py-4">
                                                    <div className="d-flex flex-column align-items-center">
                                                        <img 
                                                            src={defaultServiceImage} 
                                                            alt="No services found" 
                                                            width="80"
                                                            className="mb-3 opacity-50"
                                                        />
                                                        <h5 className="text-muted">No services found</h5>
                                                        <p className="text-muted mb-0">Try adjusting your search query</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination Controls */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div className="text-muted fs-13">
                                        Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredServices.length)} of {filteredServices.length} entries
                                    </div>
                                    <nav>
                                        <ul className="pagination pagination-primary mb-0">
                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                <button className="page-link" 
                                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>
                                                    <i className="fas fa-chevron-left"></i>
                                                </button>
                                            </li>
                                            {[...Array(totalPages)].map((_, i) => (
                                                <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                                    <button className="page-link" 
                                                            onClick={() => setCurrentPage(i + 1)}>
                                                        {i + 1}
                                                    </button>
                                                </li>
                                            ))}
                                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                <button className="page-link" 
                                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}>
                                                    <i className="fas fa-chevron-right"></i>
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            )}

                            {/* Modal for Editing Service */}
                            {modalService && (
                                <div className="modal show" style={{ display: "block", backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                                    <div className="modal-dialog modal-dialog-centered">
                                        <div className="modal-content">
                                            <div className="modal-header bg-primary text-white">
                                                <h5 className="modal-title">Edit Service</h5>
                                                <button type="button" className="btn-close btn-close-white" onClick={closeModal}></button>
                                            </div>
                                            <div className="modal-body">
                                                <form onSubmit={(e) => {
                                                    e.preventDefault();
                                                    saveChanges();
                                                }}>
                                                    <div className="mb-3">
                                                        <label className="form-label">Service Image</label>
                                                        <div className="d-flex flex-column align-items-center mb-3">
                                                            <img
                                                                src={imagePreview}
                                                                alt="Service Preview"
                                                                className="mb-2 rounded border"
                                                                style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    e.target.src = defaultServiceImage;
                                                                }}
                                                            />
                                                            <div className="mt-2">
                                                                <input
                                                                    type="file"
                                                                    className="form-control"
                                                                    accept=".jpeg,.jpg,.png,.gif"
                                                                    onChange={handleImageChange}
                                                                />
                                                                <small className="text-muted">Accepted formats: JPEG, JPG, PNG, GIF (Max 5MB)</small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="mb-3">
                                                        <label className="form-label">Title <span className="text-danger">*</span></label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={editService.title}
                                                            onChange={(e) => setEditService({ ...editService, title: e.target.value })}
                                                            required
                                                        />
                                                    </div>
                                                    <div className="mb-3">
                                                        <label className="form-label">Description <span className="text-danger">*</span></label>
                                                        <textarea
                                                            className="form-control"
                                                            value={editService.description}
                                                            onChange={(e) => setEditService({ ...editService, description: e.target.value })}
                                                            rows="3"
                                                            required
                                                        ></textarea>
                                                    </div>
                                                    <div className="mb-3">
                                                        <label className="form-label">Price ($) <span className="text-danger">*</span></label>
                                                        <div className="input-group">
                                                            <span className="input-group-text">$</span>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                value={editService.price}
                                                                onChange={(e) => setEditService({ ...editService, price: e.target.value })}
                                                                min="0"
                                                                step="0.01"
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="mb-3">
                                                        <label className="form-label">Status <span className="text-danger">*</span></label>
                                                        <select
                                                            className="form-select"
                                                            value={editService.status}
                                                            onChange={(e) => setEditService({ ...editService, status: e.target.value })}
                                                            required
                                                        >
                                                            <option value="" disabled>Select Status</option>
                                                            <option value="active">Active</option>
                                                            <option value="inactive">Inactive</option>
                                                        </select>
                                                    </div>
                                                </form>
                                            </div>
                                            <div className="modal-footer">
                                                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                                                    <i className="fas fa-times me-2"></i>Close
                                                </button>
                                                <button 
                                                    type="button" 
                                                    className="btn btn-primary" 
                                                    onClick={saveChanges}
                                                >
                                                    <i className="fas fa-save me-2"></i>Save changes
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ServiceManagement;